import { Badge, Box, Link } from "@material-ui/core";
import React from "react";
import {
  INotification,
  useNotificationListController,
} from "./NotificationList.controller";
import Accordion from "modus/components/misc/Accordion";
import { AsyncActionState } from "modus/components/form/async-actions/AsyncAction.type";
import Loader from "modus/components/misc/Loader";
import {
  ErrorMessage,
  resolveAPIErrorMessage,
} from "modus/components/error-message/ErrorMessage";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { MY_SELECTED_TEAM_ } from "header/team-selector/TeamSelector";
import { useDialogProps } from "legacy-components/dialog/Dialog";
import NotificationActions from "pages/home/notifications/components/NotificationActions/TeamNotificationActions";
import ConsentIcon from "modus/icons/ConsentIcon";
import ConsentSummary from "./ConsentSummary";

export const ConsentNotificationList: React.FC = () => {
  const { asyncResponse, refreshApprovals } = useNotificationListController({ type: "CONSENT" });
  const { open, toggle } = useDialogProps(true);
  
  return (
    <Box>
      <Accordion
        icon={<ConsentIcon color="primary" />}
        title={"User Consent Request"}
        expanded={open}
        onClick={toggle}
        className="notification-accordion"
        details={<ConsentNotificationListResponse response={asyncResponse} refreshApprovals = {refreshApprovals} />}
      />
    </Box>
  );
};

interface ConsentNotificationListResponse {
  response?: AsyncActionState<INotification[] | null | undefined>;
  refreshApprovals : () => void
}
export const ConsentNotificationListResponse: React.FC<
  ConsentNotificationListResponse
> = ({ response, refreshApprovals }) => {
  return (
    <Box style={{ width: "calc(100% - 30px)" }}>
      {response?.loading && <Loader />}
      {response?.error && (
        <ErrorMessage message={resolveAPIErrorMessage(response.error)} />
      )}

      {response?.loaded && response?.value && response?.value?.length === 0 && (
        <ErrorMessage message={"No pending approvals"} />
      )}
      {response?.loaded &&
        response?.value &&
        response?.value?.length > 0 &&
        response?.value?.map((notification: INotification) => (
          <TeamNotificationCard notification={notification} refreshApprovals={refreshApprovals}/>
        ))}
    </Box>
  );
};
interface TeamNotificationCardProps {
  notification: INotification;
  refreshApprovals: () => void;
}
export const TeamNotificationCard: React.FC<TeamNotificationCardProps> = ({
  notification,refreshApprovals
}) => {
  const currentTeam = useSelector(
    (state: any) =>
      state?.teamManager?.teams?.currentTeam?.teamName ??
      sessionStorage.getItem(MY_SELECTED_TEAM_)
  );
  const history = useHistory();
  const elapsedTime = getElapsedTime(notification.requestDate);
  return (
    <Box className="notification-card">
      <ConsentIcon color="primary" />
      <Box width="100%">
        <Box>
        An OAuth scope user consent request has been submitted by&nbsp;
          <Link
            href={`mailto:${notification?.requestedBy?.emailAddress}`}
            target="_blank"
          >
            {notification?.requestedBy?.emailAddress}{" "}
          </Link>
          {notification?.requestedBy?.accountName && (
            <span>
              from{" "}
              <b>
                <i>{notification?.requestedBy?.accountName}</i>
              </b>
            </span>
          )}
          &nbsp;and is pending for your review.
        </Box>
        <Box>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Badge className={`badge ${getColor(elapsedTime)}`}>
              {getByText(elapsedTime)}
            </Badge>
            <ConsentSummary notification={notification} refreshApprovals={refreshApprovals} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export function getByText(
  elapsedTime: { unit: string; count: number } | undefined
): string {
  if (!elapsedTime) {
    return "";
  }
  if(elapsedTime.count === 0) {
    return "Just now submitted";
  }
  if (elapsedTime.unit === "M") {
    return `${elapsedTime.count} minutes ago`;
  }
  if (elapsedTime.unit === "H") {
    return `${elapsedTime.count} hours ago`;
  }
  return `${elapsedTime.count} days ago`;
}

export function getColor(
  elapsedTime: { unit: string; count: number } | undefined
): string {
  if (!elapsedTime) {
    return "";
  }

  // Convert everything to days for comparison
  let daysEquivalent = 0;
  
  switch (elapsedTime.unit) {
    case "M": // Minutes
      daysEquivalent = elapsedTime.count / (24 * 60); // convert minutes to days
      break;
    case "H": // Hours
      daysEquivalent = elapsedTime.count / 24; // convert hours to days
      break;
    case "D": // Days
      daysEquivalent = elapsedTime.count;
      break;
    default:
      return "";
  }

  // Apply the new logic
  if (daysEquivalent <= 3) {
    return "success"; // Green for anything less than or equal to 3 days
  } else if (daysEquivalent > 3 && daysEquivalent <= 8) {
    return "warning"; // Warning for 4-8 days
  } else {
    return "error"; // Error for more than 8 days
  }
}

export function getElapsedTime(
  dateString?: string
): { unit: string; count: number } | undefined {
  if (!dateString) {
    return;
  }
  const inputDate: Date = new Date(dateString);
  const now: Date = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
  const istTime = new Date(now.getTime() - istOffset);
  const elapsedTime: number = istTime.getTime() - inputDate.getTime();

  const millisecondsInAnHour: number = 1000 * 60 * 60;
  const millisecondsInADay: number = millisecondsInAnHour * 24;
  const millisecondsInAMinute: number = 1000 * 60;

  if (elapsedTime < millisecondsInAnHour) {
    const hours: number = Math.floor(elapsedTime / millisecondsInAMinute);
    return { unit: "M", count: hours };
  } else if (elapsedTime < millisecondsInADay) {
    const hours: number = Math.floor(elapsedTime / millisecondsInAnHour);
    return { unit: "H", count: hours };
  } else {
    const days: number = Math.floor(elapsedTime / millisecondsInADay);
    return { unit: "D", count: days };
  }
}
